// src/pages/admin/AdminAssignedCandidates.jsx
// ============================================================
// Admin Panel: Recruitment & Hiring Module
// Contains two tabs:
// 1. Assigned Candidates: Reviews submitted technical evaluations from Team Leads,
//    displays candidate profile, resume, HR notes, interview details, and submits
//    final Admin decisions (Proceed / Hold / Reject).
// 2. Hiring Requisitions: Create hiring requests, track approval status,
//    review HR remarks, and open the Hiring Requisition modal.
// ============================================================

import React, { useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { useCandidates, submitAdminCandidateDecision } from "../../hooks/useRecruitment";
import { useHiringRequisitions } from "../../hooks/useHiringRequisitions";
import CreateHiringRequisitionModal from "../../components/recruitment/CreateHiringRequisitionModal";
import CandidateProfile from "../hr/recruitment/CandidateProfile";
import { toast } from "react-hot-toast";
import {
  Search,
  X,
  FileText,
  Download,
  UserCheck,
  Award,
  CheckCircle2,
  XCircle,
  PauseCircle,
  Eye,
  Phone,
  Mail,
  ShieldCheck,
  Sparkles,
  PlusCircle,
  Briefcase,
  Building2,
  Users,
  Clock,
} from "lucide-react";

export default function AdminAssignedCandidates({ defaultTab = "candidates" }) {
  const { userProfile } = useAuth();
  const adminName = userProfile?.displayName || userProfile?.name || "Admin";
  const adminId = userProfile?.uid || userProfile?.id || "";

  // Navigation Sub-Tab State
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || defaultTab;
  });

  // Candidate Data & Filters
  const { data: candidates, loading: loadingCandidates } = useCandidates();
  const [candSearch, setCandSearch] = useState("");
  const [candStatusFilter, setCandStatusFilter] = useState("all");
  const [candDeptFilter, setCandDeptFilter] = useState("all");

  // Requisitions Data & Filters
  const { data: requisitions, loading: loadingRequisitions } = useHiringRequisitions();
  const [reqSearch, setReqSearch] = useState("");
  const [reqStatusFilter, setReqStatusFilter] = useState("all");
  const [reqDeptFilter, setReqDeptFilter] = useState("all");
  const [showCreateReqModal, setShowCreateReqModal] = useState(false);

  // Candidate Profile Modal
  const [profileModalCandidate, setProfileModalCandidate] = useState(null);

  // Admin Decision Modal
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [adminDecision, setAdminDecision] = useState("Proceed to Next Round");
  const [adminRemarks, setAdminRemarks] = useState("");
  const [submittingDecision, setSubmittingDecision] = useState(false);

  // Unique departments for candidate filter
  const candDepartments = useMemo(() => {
    const set = new Set();
    candidates.forEach((c) => {
      if (c.department) set.add(c.department);
    });
    return Array.from(set);
  }, [candidates]);

  // Unique departments for requisition filter
  const reqDepartments = useMemo(() => {
    const set = new Set();
    requisitions.forEach((r) => {
      if (r.department) set.add(r.department);
    });
    return Array.from(set);
  }, [requisitions]);

  // Candidates relevant for Admin review
  const assignedCandidates = useMemo(() => {
    return candidates.filter((c) => {
      // Exclude archived candidates and completed candidates that moved to Onboarding
      if (c.archived) return false;

      const isCompleted =
        c.stage === "Selected / Moved to Onboarding" ||
        c.stage === "Moved to Onboarding" ||
        c.stage === "Hired" ||
        ((c.adminDecision === "Proceed to Next Round" || c.adminDecision === "Proceed") &&
          (c.stage === "Selected / Moved to Onboarding" || c.stage === "Moved to Onboarding"));

      if (isCompleted) return false;

      const isRelevant =
        c.stage === "Pending Admin Review" ||
        c.stage === "On Hold" ||
        c.adminDecision === "On Hold" ||
        c.adminAssigned ||
        (c.leadRecommendation && c.leadRecommendation !== "Pending Review") ||
        c.technicalRating;

      if (!isRelevant) return false;

      const matchSearch =
        (c.name || "").toLowerCase().includes(candSearch.toLowerCase()) ||
        (c.email || "").toLowerCase().includes(candSearch.toLowerCase()) ||
        (c.appliedPosition || c.role || "").toLowerCase().includes(candSearch.toLowerCase()) ||
        (c.assignedLeadName || "").toLowerCase().includes(candSearch.toLowerCase());

      const matchDept = candDeptFilter === "all" || c.department === candDeptFilter;

      const matchStatus =
        candStatusFilter === "all" ||
        (candStatusFilter === "pending" && (c.stage === "Pending Admin Review" || (!c.adminDecision && c.stage !== "On Hold"))) ||
        (candStatusFilter === "Hold" && (c.adminDecision === "On Hold" || c.stage === "On Hold")) ||
        (candStatusFilter === "Proceed" && (c.adminDecision === "Proceed to Next Round" || c.stage === "Selected / Moved to Onboarding")) ||
        (candStatusFilter === "Reject" && (c.adminDecision === "Reject" || c.stage === "Rejected"));

      return matchSearch && matchDept && matchStatus;
    });
  }, [candidates, candSearch, candDeptFilter, candStatusFilter]);

  // Key Statistics for Candidates
  const candStats = useMemo(() => {
    const total = assignedCandidates.length;
    const pending = candidates.filter((c) => (c.stage === "Pending Admin Review" || (c.leadRecommendation && !c.adminDecision)) && !c.archived && c.stage !== "Selected / Moved to Onboarding").length;
    const proceeded = candidates.filter((c) => (c.adminDecision === "Proceed to Next Round" || c.adminDecision === "Proceed" || c.stage === "Selected / Moved to Onboarding" || c.stage === "Moved to Onboarding")).length;
    const onHold = candidates.filter((c) => (c.adminDecision === "On Hold" || c.stage === "On Hold") && !c.archived).length;
    const rejected = candidates.filter((c) => c.adminDecision === "Reject" || c.stage === "Rejected").length;
    return { total, pending, proceeded, onHold, rejected };
  }, [candidates, assignedCandidates]);

  // Filtered Requisitions
  const filteredRequisitions = useMemo(() => {
    return requisitions.filter((r) => {
      const matchSearch =
        (r.title || "").toLowerCase().includes(reqSearch.toLowerCase()) ||
        (r.department || "").toLowerCase().includes(reqSearch.toLowerCase()) ||
        (r.createdByName || "").toLowerCase().includes(reqSearch.toLowerCase());

      const matchDept = reqDeptFilter === "all" || r.department === reqDeptFilter;
      const matchStatus = reqStatusFilter === "all" || r.status === reqStatusFilter;

      return matchSearch && matchDept && matchStatus;
    });
  }, [requisitions, reqSearch, reqDeptFilter, reqStatusFilter]);

  // Key Statistics for Requisitions
  const reqStats = useMemo(() => {
    const total = requisitions.length;
    const pending = requisitions.filter((r) => r.status === "Submitted").length;
    const approved = requisitions.filter((r) => r.status === "Approved" || r.status === "Converted").length;
    const rejected = requisitions.filter((r) => r.status === "Rejected").length;
    return { total, pending, approved, rejected };
  }, [requisitions]);

  function handleOpenDecisionModal(c) {
    setSelectedCandidate(c);
    setAdminDecision(
      c.adminDecision || (c.leadRecommendation?.includes("Proceed") ? "Proceed to Next Round" : "Proceed to Next Round")
    );
    setAdminRemarks(c.adminRemarks || "");
    setShowDecisionModal(true);
  }

  async function handleDecisionSubmit(e) {
    e.preventDefault();
    if (!selectedCandidate) return;
    setSubmittingDecision(true);

    try {
      await submitAdminCandidateDecision(selectedCandidate.id, {
        adminDecision,
        adminRemarks,
        adminName,
        adminId,
      });

      if (adminDecision === "Proceed to Next Round" || adminDecision === "Proceed" || adminDecision === "Move to Onboarding") {
        toast.success(`Candidate ${selectedCandidate.name} approved! Candidate is now pending HR Final Review to move to Onboarding.`);
      } else if (adminDecision === "On Hold") {
        toast.success(`Candidate ${selectedCandidate.name} placed On Hold.`);
      } else if (adminDecision === "Reject") {
        toast.success(`Candidate ${selectedCandidate.name} marked as Rejected and archived.`);
      } else {
        toast.success(`Decision saved for ${selectedCandidate.name}.`);
      }

      setShowDecisionModal(false);
      setSelectedCandidate(null);
    } catch (err) {
      toast.error("Failed to submit decision: " + err.message);
    } finally {
      setSubmittingDecision(false);
    }
  }

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <style>{`
        .admin-cand-header {
          background: linear-gradient(135deg, #1b4b31 0%, #0f2e1f 100%);
          border-radius: 16px;
          padding: 1.5rem 1.75rem;
          color: #ffffff;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 20px rgba(27, 75, 49, 0.15);
        }
        .admin-stat-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .admin-stat-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 1.1rem 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.2s ease;
        }
        .admin-stat-card:hover {
          border-color: #10b981;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.08);
        }
        .admin-stat-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #f0fdf4;
          border: 1.5px solid #bbf7d0;
          color: #15803d;
        }

        /* Sub-Tabs Bar */
        .ats-tabs-bar {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 1.5rem;
        }
        .ats-tab-btn {
          padding: 0.65rem 1.25rem;
          background: none;
          border: none;
          font-size: 0.86rem;
          cursor: pointer;
          margin-bottom: -2px;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          transition: all 0.15s ease;
        }

        /* ATS Search & Filter Bar */
        .ats-toolbar {
          display: flex;
          gap: 0.85rem;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 14px;
          padding: 0.85rem 1.1rem;
          margin-bottom: 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
        }
        .ats-search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 280px;
          background: #ffffff;
          border: 1.5px solid var(--border, #cbd5e1);
          border-radius: 10px;
          padding: 0 0.85rem;
          height: 42px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease-in-out;
        }
        .ats-search-box:focus-within {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }
        .ats-search-icon {
          color: #64748b;
          flex-shrink: 0;
          margin-right: 0.65rem;
        }
        .ats-search-box:focus-within .ats-search-icon {
          color: #1b4b31;
        }
        .ats-search-input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.86rem;
          font-weight: 500;
          color: #0f172a;
          padding: 0;
        }
        .ats-search-input::placeholder {
          color: #94a3b8;
        }
        .ats-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: #e2e8f0;
          color: #475569;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          cursor: pointer;
          padding: 0;
        }
        .ats-select-filter {
          height: 42px;
          padding: 0 0.85rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
          background: #ffffff;
          color: #0f172a;
          outline: none;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 170px;
        }
        .ats-select-filter:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }

        .requisition-toolbar {
          display: flex;
          align-items: center;
          gap: 1.1rem;
          width: 100%;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }
        .requisition-search-wrap {
          position: relative;
          flex: 1 1 68%;
          min-width: 260px;
          max-width: 68%;
        }
        .requisition-search {
          display: flex;
          align-items: center;
          width: 100%;
          height: 50px;
          background: #ffffff;
          border: 1px solid #dfe7e3;
          border-radius: 12px;
          padding: 0 0.9rem 0 0.9rem;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
          transition: all 0.2s ease;
        }
        .requisition-search:hover {
          border-color: #cfe2d9;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
        }
        .requisition-search:focus-within {
          border-color: #16936d;
          box-shadow: 0 0 0 4px rgba(22, 147, 109, 0.12), 0 6px 18px rgba(15, 23, 42, 0.06);
        }
        .requisition-search-icon {
          color: #70818a;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          margin-right: 0.75rem;
        }
        .requisition-search:focus-within .requisition-search-icon {
          color: #0f8a64;
        }
        .requisition-search-input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          color: #0f172a;
          font-size: 0.92rem;
          font-weight: 500;
          line-height: 1.2;
        }
        .requisition-search-input::placeholder {
          color: #8aa0a6;
          font-weight: 500;
        }
        .requisition-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border: none;
          border-radius: 50%;
          background: #edf2f1;
          color: #52636d;
          cursor: pointer;
          padding: 0;
          transition: all 0.18s ease;
          margin-left: 0.5rem;
        }
        .requisition-search-clear:hover {
          background: #dfeae5;
          color: #1f2d33;
        }
        .requisition-filter-group {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex: 1 1 32%;
          min-width: 0;
        }
        .requisition-filter {
          flex: 1 1 180px;
          min-width: 160px;
          max-width: 180px;
          width: 180px;
          height: 50px;
          border: 1px solid #dfe7e3;
          border-radius: 12px;
          background: #ffffff;
          padding: 0 0.9rem;
          font-size: 0.88rem;
          font-weight: 500;
          color: #0f172a;
          outline: none;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
          transition: all 0.2s ease;
          cursor: pointer;
        }
        .requisition-filter:hover {
          border-color: #cfe2d9;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
        }
        .requisition-filter:focus {
          border-color: #16936d;
          box-shadow: 0 0 0 4px rgba(22, 147, 109, 0.12), 0 6px 18px rgba(15, 23, 42, 0.05);
        }
        @media (max-width: 980px) {
          .requisition-toolbar {
            gap: 0.85rem;
          }
          .requisition-search-wrap {
            flex-basis: 100%;
            max-width: 100%;
          }
          .requisition-filter-group {
            flex-basis: 100%;
            width: 100%;
          }
          .requisition-filter {
            flex: 1 1 0;
            max-width: none;
            width: 100%;
          }
        }

        .admin-cand-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 1.25rem;
        }
        .admin-cand-card {
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 16px;
          padding: 1.35rem;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          transition: all 0.2s ease-in-out;
        }
        .admin-cand-card:hover {
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
          border-color: #cbd5e1;
        }
        .eval-notes-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.85rem 1rem;
          font-size: 0.81rem;
          color: #334155;
        }
        .lead-eval-highlight {
          background: #f0fdf4;
          border: 1.5px solid #bbf7d0;
          border-radius: 12px;
          padding: 0.9rem 1rem;
        }
        .admin-decision-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: nowrap;
          padding: 0.35rem 0.85rem;
          border-radius: 99px;
          font-size: 0.74rem;
          font-weight: 700;
          line-height: 1.2;
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          flex-shrink: 0;
        }
        .decision-Proceed { background: #f0fdf4; color: #15803d; border: 1.5px solid #bbf7d0; }
        .decision-Hold { background: #f0fdf4; color: #166534; border: 1.5px solid #86efac; }
        .decision-Reject { background: #fef2f2; color: #991b1b; border: 1.5px solid #fca5a5; }
        .decision-Pending { background: #f0fdf4; color: #15803d; border: 1.5px solid #bbf7d0; }

        .btn-decision-primary {
          background: #1b4b31;
          color: #ffffff;
          border: none;
          border-radius: 9px;
          padding: 0.55rem 1.1rem;
          font-weight: 700;
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          box-shadow: 0 2px 8px rgba(27, 75, 49, 0.2);
          transition: all 0.2s ease;
        }
        .btn-decision-primary:hover {
          background: #143724;
          transform: translateY(-1px);
        }

        /* Hiring Requisitions Cards Styling */
        .admin-req-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 1.25rem;
        }
        .admin-req-card {
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 16px;
          padding: 1.35rem;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          transition: all 0.2s ease-in-out;
        }
        .admin-req-card:hover {
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
          border-color: #cbd5e1;
        }
        .req-status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 99px;
          font-size: 0.75rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }
        .req-status-Submitted { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .req-status-Approved { background: #d1fae5; color: #065f46; border: 1px solid #a7f3d0; }
        .req-status-Converted { background: #e0f2fe; color: #0369a1; border: 1px solid #bae6fd; }
        .req-status-Rejected { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }

        .btn-create-req {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 0.6rem 1.25rem;
          font-weight: 700;
          font-size: 0.84rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          box-shadow: 0 2px 10px rgba(16, 185, 129, 0.3);
          transition: all 0.2s ease;
        }
        .btn-create-req:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.4);
        }

        /* Modal Overlay & Body Styling */
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1.25rem;
          box-sizing: border-box;
        }
        .modal-body {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 620px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 1.75rem 2rem;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15);
        }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 1.1rem; border-bottom: 1px solid #f1f5f9; margin-bottom: 1.25rem; }
        .modal-title-group { display: flex; align-items: center; gap: 0.75rem; }
        .modal-title-icon { width: 40px; height: 40px; border-radius: 10px; background: #e6f4ed; color: #1b4b31; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .modal-title { margin: 0; font-size: 1.15rem; font-weight: 700; color: #0f172a; }
        .modal-subtitle { margin: 3px 0 0 0; font-size: 0.78rem; color: #64748b; }
        .modal-close-btn {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 50%;
          border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b;
          cursor: pointer; transition: all 0.15s ease; padding: 0;
        }
        .modal-close-btn:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }

        /* Decision Choice Button Cards */
        .decision-choice-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          padding: 0.9rem 0.75rem;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          background: #ffffff;
          color: #475569;
          font-weight: 700;
          font-size: 0.84rem;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        .decision-choice-btn:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        .decision-choice-btn.selected-Proceed {
          border-color: #10b981;
          background: #ecfdf5;
          color: #065f46;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
        }
        .decision-choice-btn.selected-Hold {
          border-color: #f59e0b;
          background: #fffbeb;
          color: #92400e;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.15);
        }
        .decision-choice-btn.selected-Reject {
          border-color: #ef4444;
          background: #fef2f2;
          color: #991b1b;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
        }
      `}</style>

      {/* Enterprise Page Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "1.25rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <ShieldCheck size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            Admin - Recruitment & Hiring
          </h1>
        </div>

        {activeTab === "requisitions" && (
          <button
            style={{
              background: "#1b4b31",
              color: "#ffffff",
              border: "none",
              borderRadius: "9px",
              padding: "0.5rem 1rem",
              fontWeight: 700,
              fontSize: "0.82rem",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem"
            }}
            onClick={() => setShowCreateReqModal(true)}
          >
            <PlusCircle size={15} strokeWidth={2} /> New Hiring Requisition
          </button>
        )}
      </div>

      {/* Sub-Tabs Bar */}
      <div className="ats-tabs-bar">
        <button
          className={`ats-tab-btn ${activeTab === "candidates" ? "active" : ""}`}
          style={{
            fontWeight: 700,
            color: activeTab === "candidates" ? "#15803d" : "#64748b",
            borderBottom: activeTab === "candidates" ? "2.5px solid #15803d" : "2.5px solid transparent",
            background: activeTab === "candidates" ? "#f0fdf4" : "transparent",
            borderRadius: "8px 8px 0 0",
          }}
          onClick={() => setActiveTab("candidates")}
        >
          <Users size={16} strokeWidth={1.8} /> Assigned Candidates
        </button>
        <button
          className={`ats-tab-btn ${activeTab === "requisitions" ? "active" : ""}`}
          style={{
            fontWeight: 700,
            color: activeTab === "requisitions" ? "#15803d" : "#64748b",
            borderBottom: activeTab === "requisitions" ? "2.5px solid #15803d" : "2.5px solid transparent",
            background: activeTab === "requisitions" ? "#f0fdf4" : "transparent",
            borderRadius: "8px 8px 0 0",
          }}
          onClick={() => setActiveTab("requisitions")}
        >
          <Briefcase size={16} strokeWidth={1.8} /> Hiring Requisitions ({requisitions.length})
        </button>
      </div>

      {/* TAB 1: ASSIGNED CANDIDATES */}
      {activeTab === "candidates" && (
        <>
          {/* KPI Stats Bar */}
          <div className="admin-stat-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <Clock className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{candStats.pending}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Pending Review</div>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <CheckCircle2 className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{candStats.proceeded}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Proceeded to Next Round</div>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <PauseCircle className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{candStats.onHold}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>On Hold</div>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <XCircle className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{candStats.rejected}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Rejected</div>
              </div>
            </div>
          </div>

          {/* ATS Toolbar (Search & Filters) */}
          <div className="ats-toolbar">
            <div className="ats-search-box">
              <Search size={18} className="ats-search-icon" />
              <input
                type="text"
                className="ats-search-input"
                placeholder="Search candidate name, email, position, or lead..."
                value={candSearch}
                onChange={(e) => setCandSearch(e.target.value)}
              />
              {candSearch && (
                <button
                  type="button"
                  className="ats-search-clear"
                  onClick={() => setCandSearch("")}
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <select
              className="ats-select-filter"
              value={candDeptFilter}
              onChange={(e) => setCandDeptFilter(e.target.value)}
            >
              <option value="all">All Departments</option>
              {candDepartments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <select
              className="ats-select-filter"
              value={candStatusFilter}
              onChange={(e) => setCandStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending Admin Review</option>
              <option value="Proceed">Proceeded to Next Round</option>
              <option value="Hold">On Hold</option>
              <option value="Reject">Rejected</option>
            </select>
          </div>

          {/* Candidates List / Cards Grid */}
          {loadingCandidates ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "var(--text-muted)" }}>
              <div className="hrms-spinner" style={{ margin: "0 auto 1rem" }} />
              <p>Loading assigned candidate evaluations...</p>
            </div>
          ) : assignedCandidates.length === 0 ? (
            <div style={{ background: "var(--bg-card, #ffffff)", border: "2px dashed var(--border, #cbd5e1)", borderRadius: 16, padding: "4rem 2rem", textAlign: "center", color: "var(--text-muted)" }}>
              <UserCheck className="w-12 h-12 text-slate-300" style={{ margin: "0 auto 1rem" }} />
              <h3 style={{ fontSize: "1.1rem", fontWeight: 700, margin: "0 0 0.5rem", color: "#334155" }}>No Assigned Candidates Found</h3>
              <p style={{ fontSize: "0.85rem", margin: 0 }}>
                {candSearch || candStatusFilter !== "all" || candDeptFilter !== "all"
                  ? "No candidate evaluations match your selected filters."
                  : "When Team Leads submit technical evaluations, candidates will automatically appear here for Admin review."}
              </p>
            </div>
          ) : (
            <div className="admin-cand-grid">
              {assignedCandidates.map((c) => {
                const ratingScore = Number(c.technicalRating || 8);

                return (
                  <div key={c.id} className="admin-cand-card">
                    {/* Header Row: Candidate Info & Decision Badge */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#1b4b31", color: "#ffffff", fontWeight: 800, fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {c.name?.slice(0, 2)?.toUpperCase() || "CN"}
                        </div>
                        <div>
                          <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>{c.name}</div>
                          <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>
                            {c.appliedPosition || c.role || "Position"} · <span style={{ color: "#1b4b31" }}>{c.department || "General"}</span>
                          </div>
                        </div>
                      </div>

                      <span className={`admin-decision-badge decision-${c.adminDecision ? (c.adminDecision.includes("Proceed") ? "Proceed" : c.adminDecision.includes("Hold") ? "Hold" : "Reject") : "Pending"}`}>
                        {c.adminDecision ? c.adminDecision : "Pending Review"}
                      </span>
                    </div>

                    {/* Contact & Details Row */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", fontSize: "0.76rem", color: "#475569", borderBottom: "1px solid #f1f5f9", paddingBottom: "0.75rem" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        {c.email || "No email"}
                      </span>
                      {c.phone && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          {c.phone}
                        </span>
                      )}
                    </div>

                    {/* Resume Row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", padding: "0.6rem 0.85rem", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>
                        <FileText className="w-4 h-4 text-emerald-700" />
                        Candidate Resume
                      </div>
                      {c.resumeUrl ? (
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          <a href={c.resumeUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.25rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, borderRadius: 6, background: "#ffffff", border: "1px solid #cbd5e1", color: "#0f172a", textDecoration: "none" }}>
                            <Eye className="w-3 h-3 text-emerald-700" /> View
                          </a>
                          <a href={c.resumeUrl} download style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.25rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, borderRadius: 6, background: "#1b4b31", color: "#ffffff", textDecoration: "none" }}>
                            <Download className="w-3 h-3" /> Download
                          </a>
                        </div>
                      ) : (
                        <span style={{ fontSize: "0.74rem", color: "#94a3b8", fontStyle: "italic" }}>Resume on file</span>
                      )}
                    </div>

                    {/* HR Screening Notes */}
                    {c.hrScreeningNotes && (
                      <div className="eval-notes-box">
                        <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 3, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                          <Sparkles className="w-3 h-3 text-amber-500" /> HR Screening Notes
                        </div>
                        <div>{c.hrScreeningNotes}</div>
                      </div>
                    )}

                    {/* Team Lead Technical Evaluation Highlight Box */}
                    <div className="lead-eval-highlight">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <div style={{ fontSize: "0.8rem", fontWeight: 800, color: "#1b4b31", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <Award className="w-4 h-4 text-emerald-600" /> Technical Evaluation
                        </div>
                        <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a" }}>
                          Lead: <strong>{c.assignedLeadName || c.leadReviewedBy || "Team Lead"}</strong>
                        </span>
                      </div>

                      {/* Technical Rating Bar */}
                      <div style={{ marginBottom: "0.6rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.74rem", fontWeight: 700, marginBottom: 4 }}>
                          <span>Technical Score</span>
                          <span style={{ color: "#1b4b31" }}>{ratingScore}/10</span>
                        </div>
                        <div style={{ width: "100%", height: 7, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
                          <div style={{ width: `${(ratingScore / 10) * 100}%`, height: "100%", background: ratingScore >= 8 ? "linear-gradient(90deg, #10b981, #059669)" : ratingScore >= 6 ? "linear-gradient(90deg, #f59e0b, #d97706)" : "#ef4444", borderRadius: 99 }} />
                        </div>
                      </div>

                      {/* Lead Recommendation Pill */}
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>Lead Decision:</span>
                        <span style={{ padding: "0.18rem 0.6rem", borderRadius: 6, fontSize: "0.74rem", fontWeight: 700, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}>
                          {c.leadRecommendation || "Pending"}
                        </span>
                      </div>

                      {/* Technical Feedback */}
                      {(c.technicalFeedback || c.leadComments) && (
                        <div style={{ fontSize: "0.78rem", color: "#334155", fontStyle: "italic", background: "rgba(255,255,255,0.7)", padding: "0.5rem 0.75rem", borderRadius: 8, borderLeft: "3px solid #10b981" }}>
                          "{c.technicalFeedback || c.leadComments}"
                        </div>
                      )}
                    </div>

                    {/* Admin Remarks if submitted */}
                    {c.adminRemarks && (
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "0.6rem 0.85rem", borderRadius: 10, fontSize: "0.76rem", color: "#334155" }}>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>Admin Remarks:</span> {c.adminRemarks}
                      </div>
                    )}

                    {/* Card Action Buttons */}
                    <div style={{ display: "flex", gap: "0.5rem", marginTop: "auto", paddingTop: "0.5rem" }}>
                      <button
                        style={{ flex: 1, padding: "0.55rem 0.75rem", fontSize: "0.78rem", fontWeight: 700, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.35rem", background: "#ffffff", border: "1.5px solid #cbd5e1", color: "#475569", cursor: "pointer" }}
                        onClick={() => setProfileModalCandidate(c)}
                      >
                        <Eye className="w-3.5 h-3.5 text-emerald-700" /> Full Profile
                      </button>

                      <button
                        className="btn-decision-primary"
                        style={{ flex: 1.2, justifyContent: "center" }}
                        onClick={() => handleOpenDecisionModal(c)}
                      >
                        <ShieldCheck className="w-4 h-4" /> Final Decision
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* TAB 2: HIRING REQUISITIONS */}
      {activeTab === "requisitions" && (
        <>
          {/* KPI Stats Bar */}
          <div className="admin-stat-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <FileText className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{reqStats.total}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Total Requisitions</div>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <Clock className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{reqStats.pending}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Pending HR Review</div>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <CheckCircle2 className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{reqStats.approved}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Approved / Converted</div>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">
                <XCircle className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{reqStats.rejected}</div>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Rejected</div>
              </div>
            </div>
          </div>

          {/* Filter & Search Bar */}
          <div className="requisition-toolbar">
            <div className="requisition-search-wrap">
              <div className="requisition-search">
                <Search className="requisition-search-icon" />
                <input
                  type="text"
                  className="requisition-search-input"
                  placeholder="Search requisition title or department..."
                  value={reqSearch}
                  onChange={(e) => setReqSearch(e.target.value)}
                />
                {reqSearch && (
                  <button
                    type="button"
                    className="requisition-search-clear"
                    onClick={() => setReqSearch("")}
                    title="Clear search"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>

            <div className="requisition-filter-group">
              <select
                className="requisition-filter"
                value={reqDeptFilter}
                onChange={(e) => setReqDeptFilter(e.target.value)}
              >
                <option value="all">All Departments</option>
                {reqDepartments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>

              <select
                className="requisition-filter"
                value={reqStatusFilter}
                onChange={(e) => setReqStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Submitted">Pending HR Review</option>
                <option value="Approved">Approved</option>
                <option value="Converted">Converted to Job Opening</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Requisitions Grid */}
          {loadingRequisitions ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "var(--text-muted)" }}>
              <div className="hrms-spinner" style={{ margin: "0 auto 1rem" }} />
              <p>Loading hiring requisitions...</p>
            </div>
          ) : filteredRequisitions.length === 0 ? (
            <div style={{ background: "var(--bg-card, #ffffff)", border: "2px dashed var(--border, #cbd5e1)", borderRadius: 16, padding: "4rem 2rem", textAlign: "center", color: "var(--text-muted)" }}>
              <Briefcase className="w-12 h-12 text-slate-300" style={{ margin: "0 auto 1rem" }} />
              <h3 style={{ fontSize: "1.1rem", fontWeight: 700, margin: "0 0 0.5rem", color: "#334155" }}>No Hiring Requisitions Found</h3>
              <p style={{ fontSize: "0.85rem", margin: "0 0 1.25rem" }}>
                {reqSearch || reqStatusFilter !== "all" || reqDeptFilter !== "all"
                  ? "No requisitions match your selected search or filters."
                  : "Create your first hiring requisition to request headcount for your team."}
              </p>
              <button className="btn-create-req" style={{ margin: "0 auto" }} onClick={() => setShowCreateReqModal(true)}>
                <PlusCircle className="w-4 h-4" /> Create Hiring Requisition
              </button>
            </div>
          ) : (
            <div className="admin-req-grid">
              {filteredRequisitions.map((req) => {
                const skillsArr = Array.isArray(req.skills)
                  ? req.skills
                  : typeof req.skills === "string"
                  ? req.skills.split(",").map((s) => s.trim())
                  : [];

                return (
                  <div key={req.id} className="admin-req-card">
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                      <div>
                        <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>{req.title}</div>
                        <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600, marginTop: 2 }}>
                          <Building2 className="w-3.5 h-3.5 text-slate-400 inline mr-1" />
                          {req.department} · <Users className="w-3.5 h-3.5 text-slate-400 inline ml-1 mr-1" /> {req.openings || 1} opening(s)
                        </div>
                      </div>

                      <span className={`req-status-badge req-status-${req.status}`}>
                        {req.status === "Submitted" ? "Pending HR" : req.status === "Converted" ? "Job Created" : req.status}
                      </span>
                    </div>

                    {/* Priority & Type Pills */}
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", fontSize: "0.74rem" }}>
                      <span style={{ padding: "0.15rem 0.55rem", borderRadius: 6, fontWeight: 700, background: "#f1f5f9", color: "#334155" }}>
                        {req.employmentType || "Full-Time"}
                      </span>
                      <span style={{ padding: "0.15rem 0.55rem", borderRadius: 6, fontWeight: 700, background: req.priority === "High" || req.priority === "Urgent" ? "#fee2e2" : "#fef3c7", color: req.priority === "High" || req.priority === "Urgent" ? "#dc2626" : "#b45309" }}>
                        Priority: {req.priority || "Medium"}
                      </span>
                      {req.experience && (
                        <span style={{ padding: "0.15rem 0.55rem", borderRadius: 6, fontWeight: 700, background: "#f0fdf4", color: "#15803d" }}>
                          Exp: {req.experience}
                        </span>
                      )}
                      {req.salaryRange && (
                        <span style={{ padding: "0.15rem 0.55rem", borderRadius: 6, fontWeight: 700, background: "#f0f9ff", color: "#0369a1" }}>
                          Budget: {req.salaryRange}
                        </span>
                      )}
                    </div>

                    {/* Justification Box */}
                    {req.justification && (
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "0.75rem", fontSize: "0.78rem", color: "#334155" }}>
                        <div style={{ fontWeight: 700, color: "#64748b", fontSize: "0.72rem", marginBottom: 2 }}>JUSTIFICATION</div>
                        <div>{req.justification}</div>
                      </div>
                    )}

                    {/* Skills tags */}
                    {skillsArr.length > 0 && (
                      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                        {skillsArr.map((sk, idx) => (
                          <span key={idx} style={{ background: "#e2e8f0", color: "#334155", padding: "0.15rem 0.5rem", borderRadius: 6, fontSize: "0.72rem", fontWeight: 600 }}>
                            {sk}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* HR Remarks / Approval info */}
                    {req.hrRemarks && (
                      <div style={{ background: req.status === "Rejected" ? "#fff1f2" : "#f0fdf4", border: `1px solid ${req.status === "Rejected" ? "#fecdd3" : "#bbf7d0"}`, borderRadius: 10, padding: "0.65rem 0.85rem", fontSize: "0.76rem" }}>
                        <span style={{ fontWeight: 700, color: req.status === "Rejected" ? "#991b1b" : "#166534" }}>
                          HR Review ({req.hrReviewedByName || "HR"}):
                        </span>{" "}
                        {req.hrRemarks}
                      </div>
                    )}

                    {/* Footer Metadata */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: "0.5rem", fontSize: "0.74rem", color: "#94a3b8", borderTop: "1px solid #f1f5f9" }}>
                      <span>Requested by: <strong>{req.createdByName || "Admin"}</strong></span>
                      <span>{req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString() : "Recent"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Enterprise Admin Final Decision Modal */}
      {showDecisionModal && selectedCandidate && (
        <div className="modal-overlay" onClick={() => setShowDecisionModal(false)}>
          <div className="modal-body" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-title-icon">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h3 className="modal-title">Admin Final Decision</h3>
                  <p className="modal-subtitle">
                    Candidate: <strong>{selectedCandidate.name}</strong> · Position: <strong>{selectedCandidate.appliedPosition || selectedCandidate.role || "Applicant"}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowDecisionModal(false)}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Clean Team Lead Evaluation Summary Card */}
            <div style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #f7fee7 100%)", border: "1.5px solid #bbf7d0", borderRadius: 14, padding: "1.1rem 1.25rem", marginBottom: "1.35rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 800, color: "#1b4b31", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <Award size={18} className="text-emerald-700" /> Team Lead Technical Evaluation
                </div>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#0f172a" }}>
                  Lead: <strong>{selectedCandidate.assignedLeadName || selectedCandidate.leadReviewedBy || "Team Lead"}</strong>
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.6rem" }}>
                <div style={{ background: "#ffffff", padding: "0.55rem 0.85rem", borderRadius: 8, border: "1px solid #dcfce7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "0.75rem", color: "#475569", fontWeight: 600 }}>Technical Score</span>
                  <span style={{ fontSize: "0.9rem", fontWeight: 800, color: "#1b4b31" }}>{selectedCandidate.technicalRating || 8}/10</span>
                </div>

                <div style={{ background: "#ffffff", padding: "0.55rem 0.85rem", borderRadius: 8, border: "1px solid #dcfce7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "0.75rem", color: "#475569", fontWeight: 600 }}>Recommendation</span>
                  <span style={{ fontSize: "0.78rem", fontWeight: 800, padding: "0.15rem 0.55rem", borderRadius: 6, background: selectedCandidate.leadRecommendation?.includes("Proceed") ? "#d1fae5" : selectedCandidate.leadRecommendation === "Hold" ? "#fef3c7" : "#fee2e2", color: selectedCandidate.leadRecommendation?.includes("Proceed") ? "#065f46" : selectedCandidate.leadRecommendation === "Hold" ? "#92400e" : "#991b1b" }}>
                    {selectedCandidate.leadRecommendation || "Proceed"}
                  </span>
                </div>
              </div>

              {(selectedCandidate.technicalFeedback || selectedCandidate.leadComments) && (
                <div style={{ fontSize: "0.8rem", color: "#1e293b", fontStyle: "italic", background: "rgba(255, 255, 255, 0.85)", padding: "0.6rem 0.85rem", borderRadius: 8, borderLeft: "3.5px solid #10b981" }}>
                  "{selectedCandidate.technicalFeedback || selectedCandidate.leadComments}"
                </div>
              )}
            </div>

            {/* Admin Decision Form */}
            <form onSubmit={handleDecisionSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#0f172a", marginBottom: "0.6rem" }}>
                  Final Candidate Status <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem" }}>
                  {[
                    { id: "Proceed to Next Round", key: "Proceed", label: "Proceed", desc: "Advance candidate", icon: CheckCircle2, color: "#10b981" },
                    { id: "On Hold", key: "Hold", label: "On Hold", desc: "Keep candidate on hold", icon: PauseCircle, color: "#f59e0b" },
                    { id: "Reject", key: "Reject", label: "Reject", desc: "Reject application", icon: XCircle, color: "#ef4444" },
                  ].map((opt) => {
                    const IconComp = opt.icon;
                    const isSelected = adminDecision === opt.id;
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        className={`decision-choice-btn ${isSelected ? `selected-${opt.key}` : ""}`}
                        onClick={() => setAdminDecision(opt.id)}
                      >
                        <IconComp size={22} style={{ color: opt.color }} />
                        <div>{opt.label}</div>
                        <div style={{ fontSize: "0.68rem", fontWeight: 500, opacity: 0.8 }}>{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" }}>
                  Admin Remarks & Instructions
                </label>
                <textarea
                  className="ats-input"
                  rows={3}
                  style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, padding: "0.75rem", fontSize: "0.86rem", resize: "vertical", height: 90 }}
                  placeholder="Enter administrative feedback, next round schedule notes, or offer guidelines..."
                  value={adminRemarks}
                  onChange={(e) => setAdminRemarks(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #f1f5f9" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ borderRadius: 9, padding: "0.6rem 1.25rem", height: 42 }}
                  onClick={() => setShowDecisionModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  style={{ height: 42, padding: "0 1.4rem" }}
                  disabled={submittingDecision}
                >
                  <ShieldCheck size={18} /> {submittingDecision ? "Saving Decision..." : "Save Final Decision"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complete Candidate Profile Modal */}
      {profileModalCandidate && (
        <CandidateProfile
          candidate={profileModalCandidate}
          onClose={() => setProfileModalCandidate(null)}
        />
      )}

      {/* Reusable Hiring Requisition Modal */}
      <CreateHiringRequisitionModal
        isOpen={showCreateReqModal}
        onClose={() => setShowCreateReqModal(false)}
      />
    </Layout>
  );
}
